/**
 * Platform detection for MaxGlassTrack.
 *
 * Capacitor is loaded dynamically so this module works in both plain web
 * builds (no Capacitor) and native Android/iOS builds (with Capacitor).
 * When running plain web, `isNative()` returns false and all native-specific
 * code paths fall back to their web equivalents.
 *
 * Usage:
 *   import { isNative, platformName } from "@/lib/platform";
 *   if (isNative()) { ... use native plugin ... } else { ... web fallback ... }
 */

// We peek at the global Capacitor object injected by the native bridge at
// runtime. No static import — keeps the web bundle free of Capacitor code.
interface CapacitorWindow extends Window {
  Capacitor?: {
    isNativePlatform: () => boolean;
    getPlatform: () => "ios" | "android" | "web";
    Plugins: Record<string, any>;
  };
}

export function isNative(): boolean {
  const cap = (window as CapacitorWindow).Capacitor;
  return !!cap && cap.isNativePlatform();
}

export function platformName(): "ios" | "android" | "web" {
  const cap = (window as CapacitorWindow).Capacitor;
  if (!cap) return "web";
  return cap.getPlatform();
}

export function isAndroid(): boolean {
  return platformName() === "android";
}

export function isIOS(): boolean {
  return platformName() === "ios";
}
